import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import {
  GlobalContext,
  OPEN_WIDGET,
  CLOSE_WIDGET,
} from '@mummssoftware/global-context';
import {
  CarePlanFrequency,
  ComplianceView,
} from '@mummssoftware/utils/constants';
import { CarePlanFrequencyContext } from '../../context';
import defaults from '../../translations/en.json';

/**
 * "Compliance View" opens the scheduled-visits compliance calendar as an
 * expanded dashboard widget side by side with Care Plan - Visit Frequency,
 * the same way widgets opened from the sidebar sit next to each other. From
 * the grid tile it expands CPVF too. Pressing it again (or the calendar's
 * header checkmark) closes the calendar, and so does closing expanded CPVF.
 */

const ComplianceButton = styled(Button)`
  && {
    margin-left: 8px;
    padding: 2px 8px;
    font-size: 0.75rem;
    line-height: 1.4;
    text-transform: none;
    white-space: nowrap;
    color: ${(props) => props.theme.palette.text.primary};
    border-color: ${(props) => props.theme.palette.text.primary};
  }

  /* Pressed while the calendar is open: invert text and paper so it reads in
     every theme (MUI's contained gray kept the light text of blueberry/dark). */
  &&[aria-pressed='true'],
  &&[aria-pressed='true']:hover {
    color: ${(props) => props.theme.palette.background.paper};
    background-color: ${(props) => props.theme.palette.text.primary};
  }

  && .MuiButton-startIcon {
    margin-right: 4px;
  }

  && .MuiButton-startIcon > svg {
    font-size: 1rem;
  }
`;

export const ComplianceViewButton = () => {
  const { patient, smallWidget, appTitle } = useContext(
    CarePlanFrequencyContext,
  );
  const { dispatch: dispatchGlobal, widgetNamesOpen = [] } = useContext(
    GlobalContext,
  );

  // The expanded CPVF instance unmounts when CPVF closes; take the calendar
  // with it so it is not left open on its own. The grid tile stays mounted.
  useEffect(
    () => () => {
      if (!smallWidget) {
        dispatchGlobal({
          type: CLOSE_WIDGET,
          payload: { componentName: ComplianceView },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [smallWidget],
  );

  if (!patient) return null;

  const calendarOpen = widgetNamesOpen.includes(ComplianceView);

  const toggle = () => {
    if (calendarOpen) {
      dispatchGlobal({
        type: CLOSE_WIDGET,
        payload: { componentName: ComplianceView },
      });
      return;
    }
    if (!widgetNamesOpen.includes(CarePlanFrequency)) {
      dispatchGlobal({
        type: OPEN_WIDGET,
        payload: { componentName: CarePlanFrequency, widgetName: appTitle },
      });
    }
    dispatchGlobal({
      type: OPEN_WIDGET,
      payload: {
        componentName: ComplianceView,
        widgetName: defaults.complianceViewTitle,
      },
    });
  };

  return (
    <ComplianceButton
      size="small"
      variant="outlined"
      startIcon={smallWidget ? undefined : <EventAvailableIcon />}
      onClick={toggle}
      data-testid="cpf-compliance-view-button"
      title={defaults.complianceView}
      aria-pressed={calendarOpen}
    >
      {smallWidget ? <EventAvailableIcon /> : defaults.complianceView}
    </ComplianceButton>
  );
};

export default ComplianceViewButton;
